/**
 * Offline Database Manager using IndexedDB
 * Handles local storage for offline-first functionality
 */

const DB_NAME = 'WorkSafeAgentOffline';
const DB_VERSION = 1;

// Store names
const STORES = {
  INCIDENTS: 'incidents',
  PTPS: 'ptps',
  JHAS: 'jhas',
  NEAR_MISSES: 'near_misses',
  SYNC_QUEUE: 'sync_queue',
  CACHE: 'cache',
  USER_DATA: 'user_data',
  AI_SCANS: 'ai_scans'
};

class OfflineDB {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineDB] Failed to open database');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Incidents store
        if (!db.objectStoreNames.contains(STORES.INCIDENTS)) {
          const incidentStore = db.createObjectStore(STORES.INCIDENTS, { keyPath: 'id' });
          incidentStore.createIndex('status', 'status', { unique: false });
          incidentStore.createIndex('created_at', 'created_at', { unique: false });
          incidentStore.createIndex('synced', 'synced', { unique: false });
        }

        // PTPs store
        if (!db.objectStoreNames.contains(STORES.PTPS)) {
          const ptpStore = db.createObjectStore(STORES.PTPS, { keyPath: 'id' });
          ptpStore.createIndex('date', 'date', { unique: false });
          ptpStore.createIndex('synced', 'synced', { unique: false });
        }

        // JHAs store
        if (!db.objectStoreNames.contains(STORES.JHAS)) {
          const jhaStore = db.createObjectStore(STORES.JHAS, { keyPath: 'id' });
          jhaStore.createIndex('status', 'status', { unique: false });
          jhaStore.createIndex('synced', 'synced', { unique: false });
        }

        // Near Misses store
        if (!db.objectStoreNames.contains(STORES.NEAR_MISSES)) {
          const nmStore = db.createObjectStore(STORES.NEAR_MISSES, { keyPath: 'id' });
          nmStore.createIndex('date_occurred', 'date_occurred', { unique: false });
          nmStore.createIndex('synced', 'synced', { unique: false });
        }

        // Sync Queue store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('created_at', 'created_at', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
        }

        // Cache store for API responses
        if (!db.objectStoreNames.contains(STORES.CACHE)) {
          const cacheStore = db.createObjectStore(STORES.CACHE, { keyPath: 'key' });
          cacheStore.createIndex('expires_at', 'expires_at', { unique: false });
        }

        // User data store
        if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
          db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
        }

        // AI Scans store for offline hazard scanning
        if (!db.objectStoreNames.contains(STORES.AI_SCANS)) {
          const aiScansStore = db.createObjectStore(STORES.AI_SCANS, { keyPath: 'id' });
          aiScansStore.createIndex('status', 'status', { unique: false });
          aiScansStore.createIndex('created_at', 'created_at', { unique: false });
          aiScansStore.createIndex('synced', 'synced', { unique: false });
        }
      };
    });
  }

  // Generic CRUD operations
  async add(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add({ ...data, synced: false, offline_created: true });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, id) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, id) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getByIndex(storeName, indexName, value) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  // Sync Queue operations
  async addToSyncQueue(type, action, data, endpoint) {
    const queueItem = {
      type,
      action, // 'create', 'update', 'delete'
      data,
      endpoint,
      status: 'pending',
      created_at: new Date().toISOString(),
      retries: 0,
      max_retries: 3
    };
    return this.add(STORES.SYNC_QUEUE, queueItem);
  }

  async getPendingSyncItems() {
    return this.getByIndex(STORES.SYNC_QUEUE, 'status', 'pending');
  }

  async updateSyncItemStatus(id, status, error = null) {
    const item = await this.get(STORES.SYNC_QUEUE, id);
    if (item) {
      item.status = status;
      item.last_attempt = new Date().toISOString();
      if (error) item.error = error;
      if (status === 'failed') item.retries = (item.retries || 0) + 1;
      await this.put(STORES.SYNC_QUEUE, item);
    }
  }

  async removeSyncItem(id) {
    return this.delete(STORES.SYNC_QUEUE, id);
  }

  // Cache operations
  async cacheResponse(key, data, ttlMinutes = 60) {
    const cacheItem = {
      key,
      data,
      cached_at: new Date().toISOString(),
      expires_at: new Date(Date.now() + ttlMinutes * 60 * 1000).toISOString()
    };
    return this.put(STORES.CACHE, cacheItem);
  }

  async getCachedResponse(key) {
    const item = await this.get(STORES.CACHE, key);
    if (!item) return null;
    if (new Date(item.expires_at) < new Date()) {
      await this.delete(STORES.CACHE, key);
      return null;
    }
    return item.data;
  }

  // User data operations
  async setUserData(key, value) {
    return this.put(STORES.USER_DATA, { key, value });
  }

  async getUserData(key) {
    const item = await this.get(STORES.USER_DATA, key);
    return item?.value;
  }
}

// Singleton instance
const offlineDB = new OfflineDB();

export { offlineDB, STORES };
export default offlineDB;
